import type { GROUPED_DATA_CALCULATOR_STRINGS } from "@/components/tools/grouped-data-calculator";
import {
  Explanation,
  Formula,
  WorkingPanel,
} from "@/components/tools/stats-descriptive/ui";
import { HistogramPlot } from "@/components/tools/stats-descriptive-more/histogram-plot";
import { t } from "@/lib/i18n/interpolate";
import type { NumberFormatter } from "@/lib/tool-kit/stats-descriptive/format";
import type {
  GroupedReport,
  ModeStep,
  QuantileStep,
} from "@/lib/tool-kit/stats-descriptive-more/grouped";

type Strings = typeof GROUPED_DATA_CALCULATOR_STRINGS;

export interface GroupedView {
  strings: Strings;
  fmt: NumberFormatter;
  num: (value: number) => string;
}

function range(from: number, to: number, view: GroupedView): string {
  return t(view.strings.rangeTemplate, {
    from: view.num(from),
    to: view.num(to),
  });
}

/** Display cells for the class table: limits, boundaries, f, x, fx, fx², cumulative. */
export function tableRows(
  report: GroupedReport,
  view: GroupedView
): string[][] {
  const { fmt, num } = view;
  return report.classes.map((each) => [
    range(each.lower, each.upper, view),
    range(each.lowerBoundary, each.upperBoundary, view),
    num(each.freq),
    num(each.midpoint),
    num(each.fx),
    num(each.fx2),
    fmt(each.cumulative),
  ]);
}

export function tableTotals(
  report: GroupedReport,
  view: GroupedView
): string[] {
  const { strings, num } = view;
  return [
    strings.total,
    "",
    num(report.n),
    "",
    num(report.sumFx),
    num(report.sumFx2),
    "",
  ];
}

export function statisticRows(
  report: GroupedReport,
  view: GroupedView
): string[][] {
  const { strings, num } = view;
  const optional = (value: number | null) =>
    value === null ? strings.notAvailable : num(value);
  return [
    [strings.statMean, num(report.mean)],
    [strings.statMedian, num(report.median.value)],
    [strings.statQ1, num(report.q1.value)],
    [strings.statQ3, num(report.q3.value)],
    [strings.statIqr, num(report.q3.value - report.q1.value)],
    [
      strings.statMode,
      report.modes.map((each) => num(each.value)).join(strings.modeSeparator),
    ],
    [strings.statVarianceSample, optional(report.varianceSample)],
    [strings.statSdSample, optional(report.sdSample)],
    [strings.statVariancePop, num(report.variancePopulation)],
    [strings.statSdPop, num(report.sdPopulation)],
  ];
}

function quantileLine(
  step: QuantileStep,
  name: string,
  report: GroupedReport,
  view: GroupedView
): string {
  const { num } = view;
  const chosen = report.classes[step.classIndex];
  return t(view.strings.workQuantile, {
    name,
    p: num(step.p),
    target: num(step.target),
    from: num(chosen.lowerBoundary),
    to: num(chosen.upperBoundary),
    cf: num(step.cumulativeBefore),
    f: num(step.freq),
    w: num(step.width),
    value: num(step.value),
  });
}

function modeLine(
  step: ModeStep,
  report: GroupedReport,
  view: GroupedView
): string {
  const { strings, num } = view;
  const chosen = report.classes[step.classIndex];
  const values = {
    from: num(chosen.lowerBoundary),
    to: num(chosen.upperBoundary),
    f1: num(step.f1),
    f0: num(step.f0),
    f2: num(step.f2),
    d1: num(step.delta1),
    d2: num(step.delta2),
    w: num(step.width),
    value: num(step.value),
  };
  return t(
    step.delta1 + step.delta2 === 0 ? strings.workModeFlat : strings.workMode,
    values
  );
}

export function GroupedWorking({
  report,
  view,
}: {
  report: GroupedReport;
  view: GroupedView;
}) {
  const { strings, fmt, num } = view;
  const first = report.classes[0];
  return (
    <WorkingPanel title={strings.workingTitle}>
      {report.boundaryAdjustment > 0 ? (
        <Explanation>
          {t(strings.workBoundaries, {
            gap: num(report.boundaryAdjustment * 2),
            half: num(report.boundaryAdjustment),
            example:
              range(first.lower, first.upper, view) +
              " → " +
              range(first.lowerBoundary, first.upperBoundary, view),
          })}
        </Explanation>
      ) : null}
      <Formula>
        {t(strings.workMean, {
          sumFx: num(report.sumFx),
          n: fmt(report.n),
          mean: num(report.mean),
        })}
      </Formula>
      {report.varianceSample === null ? (
        <Formula>
          {t(strings.workVarianceOne, {
            variance: num(report.variancePopulation),
          })}
        </Formula>
      ) : (
        <>
          <Formula>
            {t(strings.workVariance, {
              sumFx2: num(report.sumFx2),
              sumFx: num(report.sumFx),
              n: fmt(report.n),
              variance: num(report.varianceSample),
            })}
          </Formula>
          <Formula>
            {t(strings.workSd, {
              variance: num(report.varianceSample),
              sd: num(report.sdSample ?? Number.NaN),
            })}
          </Formula>
        </>
      )}
      <Formula>
        {quantileLine(report.median, strings.nameMedian, report, view)}
      </Formula>
      <Formula>{quantileLine(report.q1, strings.nameQ1, report, view)}</Formula>
      <Formula>{quantileLine(report.q3, strings.nameQ3, report, view)}</Formula>
      {report.modes.map((step) => (
        <Formula key={step.classIndex}>{modeLine(step, report, view)}</Formula>
      ))}
      {report.equalWidths ? null : (
        <Explanation>{strings.workDensityNote}</Explanation>
      )}
    </WorkingPanel>
  );
}

/** The headline strings shared by the cards and the copied summary. */
export function headline(report: GroupedReport, view: GroupedView) {
  const { strings, num } = view;
  const optional = (value: number | null) =>
    value === null ? strings.notAvailable : num(value);
  return {
    mode: report.modes
      .map((each) => num(each.value))
      .join(strings.modeSeparator),
    sd: optional(report.sdSample),
    variance: optional(report.varianceSample),
  };
}

export function GroupedPlot({
  report,
  view,
}: {
  report: GroupedReport;
  view: GroupedView;
}) {
  const { strings, fmt, num } = view;
  const equal = report.equalWidths;
  return (
    <HistogramPlot
      bins={report.classes.map((each) => ({
        from: each.lowerBoundary,
        to: each.upperBoundary,
        count: equal ? each.freq : each.freq / each.width,
      }))}
      fmt={fmt}
      label={t(strings.plotLabel, {
        mean: num(report.mean),
        median: num(report.median.value),
        mode: num(report.modes[0].value),
      })}
      markers={[
        { value: report.mean, label: strings.markMean },
        { value: report.median.value, label: strings.markMedian, dashed: true },
        { value: report.modes[0].value, label: strings.markMode },
      ]}
      yLabel={equal ? strings.plotYLabel : strings.plotYDensity}
    />
  );
}
